import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, resolveImage } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Upload, X, Youtube, Film } from "lucide-react";

const QUARTERS = [
  { value: 1, label: "Q1 — The Warm-Up" },
  { value: 2, label: "Q2 — The Playbook" },
  { value: 3, label: "Q3 — The Full-Court Press" },
  { value: 4, label: "Q4 — The Buzzer Beater" },
];

const Field = ({ label, name, value, onChange, type = "text", placeholder, testid, full }) => (
  <label className={`block ${full ? "md:col-span-2" : ""}`}>
    <span className="block text-[10px] tracking-[0.28em] uppercase text-black/50 mb-3">{label}</span>
    <input
      data-testid={testid}
      type={type}
      name={name}
      value={value || ""}
      onChange={onChange}
      placeholder={placeholder}
      className="w-full bg-transparent border-0 border-b border-black/20 rounded-none px-0 py-3 text-base focus:outline-none focus:border-black transition-colors"
    />
  </label>
);

const GamePlanForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  const posterRef = useRef(null);
  const videoRef = useRef(null);

  const [saving, setSaving] = useState(false);
  const [uploadingPoster, setUploadingPoster] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [form, setForm] = useState({
    quarter: 1,
    title: "",
    subtitle: "",
    description: "",
    youtube_url: "",
    video_url: "",
    poster_url: "",
    is_published: true,
  });

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const { data } = await api.get("/admin/game-plan");
        const found = data.find((q) => q.id === id);
        if (!found) { toast.error("Quarter not found"); navigate("/admin"); return; }
        setForm({
          quarter: found.quarter,
          title: found.title,
          subtitle: found.subtitle || "",
          description: found.description || "",
          youtube_url: found.youtube_url || "",
          video_url: found.video_url || "",
          poster_url: found.poster_url || "",
          is_published: found.is_published !== false,
        });
      } catch { toast.error("Failed to load quarter"); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({
      ...f,
      [name]: type === "checkbox" ? checked : (name === "quarter" ? Number(value) || 1 : value),
    }));
  };

  const handlePosterUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPoster(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, poster_url: data.url }));
      toast.success("Poster uploaded");
    } catch { toast.error("Upload failed"); }
    finally {
      setUploadingPoster(false);
      if (posterRef.current) posterRef.current.value = "";
    }
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingVideo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload-video", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, video_url: data.url }));
      toast.success("Video uploaded");
    } catch { toast.error("Video upload failed"); }
    finally {
      setUploadingVideo(false);
      if (videoRef.current) videoRef.current.value = "";
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/admin/game-plan/${id}`, form);
        toast.success("Quarter updated");
      } else {
        await api.post("/admin/game-plan", form);
        toast.success("Quarter added");
      }
      navigate("/admin");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <main data-testid="admin-gameplan-form" className="min-h-screen bg-white pb-32">
      <header className="border-b border-black/10 px-6 md:px-12 py-6 sticky top-0 bg-white z-10">
        <Link to="/admin" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] hover:opacity-60">
          <ArrowLeft size={14} /> Dashboard
        </Link>
      </header>

      <div className="max-w-[1000px] mx-auto px-6 md:px-12 pt-16">
        <p className="overline mb-4">{isEdit ? "Edit quarter" : "New quarter"}</p>
        <h1 className="font-heading text-4xl md:text-5xl tracking-tight mb-16">
          {isEdit ? <>Update <em className="italic font-light">quarter</em></> : <>Add a <em className="italic font-light">quarter</em></>}
        </h1>

        <form onSubmit={handleSave} className="space-y-16">
          <section>
            <p className="overline mb-8">Basics</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8">
              <label className="block">
                <span className="block text-[10px] tracking-[0.28em] uppercase text-black/50 mb-3">Quarter *</span>
                <select
                  name="quarter"
                  value={form.quarter}
                  onChange={handleChange}
                  data-testid="gp-quarter"
                  className="w-full bg-transparent border-0 border-b border-black/20 rounded-none px-0 py-3 text-base focus:outline-none focus:border-black transition-colors"
                >
                  {QUARTERS.map((q) => (
                    <option key={q.value} value={q.value}>{q.label}</option>
                  ))}
                </select>
              </label>
              <Field label="Title *" name="title" value={form.title} onChange={handleChange} placeholder="The Warm-Up" testid="gp-title" />
              <Field label="Subtitle" name="subtitle" value={form.subtitle} onChange={handleChange} placeholder="Pregame preparation" testid="gp-subtitle" full />
            </div>
          </section>

          <section>
            <p className="overline mb-8">Description</p>
            <textarea
              data-testid="gp-description"
              name="description"
              value={form.description}
              onChange={handleChange}
              rows={6}
              placeholder="Explain what this quarter covers…"
              className="w-full bg-transparent border border-black/15 px-4 py-3 text-base focus:outline-none focus:border-black transition-colors resize-y"
            />
          </section>

          <section>
            <p className="overline mb-8">Video</p>

            <div className="mb-8">
              <span className="text-[10px] tracking-[0.28em] uppercase text-black/50 mb-3 flex items-center gap-2">
                <Film size={14} /> Upload video file (preferred)
              </span>
              {form.video_url ? (
                <div className="flex items-center gap-4 mt-3">
                  <video src={resolveImage(form.video_url)} controls className="w-64 aspect-video bg-black" data-testid="gp-video-preview" />
                  <button
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, video_url: "" }))}
                    data-testid="gp-video-remove"
                    className="inline-flex items-center gap-2 px-4 py-2 border border-black/15 hover:border-red-400 hover:text-red-600 text-[11px] uppercase tracking-[0.24em] transition-colors"
                  >
                    <X size={14} /> Remove
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => videoRef.current?.click()}
                  disabled={uploadingVideo}
                  data-testid="gp-video-upload"
                  className="inline-flex items-center gap-2 px-6 py-3 mt-3 border border-black/15 hover:border-black text-[11px] uppercase tracking-[0.24em] transition-colors disabled:opacity-50"
                >
                  <Upload size={14} /> {uploadingVideo ? "Uploading & compressing…" : "Upload MP4"}
                </button>
              )}
              <input ref={videoRef} type="file" accept="video/*" onChange={handleVideoUpload} className="hidden" />
            </div>

            <label className="block mb-6">
              <span className="text-[10px] tracking-[0.28em] uppercase text-black/50 mb-3 flex items-center gap-2">
                <Youtube size={14} /> Or paste a YouTube URL
              </span>
              <input
                type="url"
                name="youtube_url"
                value={form.youtube_url}
                onChange={handleChange}
                placeholder="https://www.youtube.com/watch?v=…"
                data-testid="gp-youtube-url"
                className="w-full bg-transparent border-0 border-b border-black/20 rounded-none px-0 py-3 text-base focus:outline-none focus:border-black transition-colors"
              />
            </label>
            <p className="text-xs text-black/50 max-w-2xl">
              An uploaded video file always takes priority over a YouTube link. While both
              are empty, the quarter shows an elegant &quot;Coming soon&quot; placeholder on
              the public page.
            </p>
          </section>

          <section>
            <p className="overline mb-8">Poster image (optional)</p>
            <div className="flex flex-col sm:flex-row items-start gap-8">
              {form.poster_url ? (
                <div className="relative w-48 aspect-video bg-gray-100">
                  <img src={resolveImage(form.poster_url)} alt="Poster" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, poster_url: "" }))}
                    className="absolute -top-2 -right-2 bg-white border border-black/15 rounded-full w-8 h-8 flex items-center justify-center hover:bg-red-50 hover:text-red-600"
                  >
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <div className="w-48 aspect-video bg-gray-100 flex items-center justify-center text-[10px] uppercase tracking-widest text-black/30 text-center">
                  No poster
                </div>
              )}
              <div className="flex-1">
                <button
                  type="button"
                  onClick={() => posterRef.current?.click()}
                  disabled={uploadingPoster}
                  data-testid="gp-poster-upload"
                  className="inline-flex items-center gap-2 px-6 py-3 border border-black/15 hover:border-black text-[11px] uppercase tracking-[0.24em] transition-colors disabled:opacity-50"
                >
                  <Upload size={14} /> {uploadingPoster ? "Uploading…" : form.poster_url ? "Replace poster" : "Upload poster"}
                </button>
                <p className="text-xs text-black/40 mt-3 max-w-md">
                  Shown behind the play button before video loads. 16:9 landscape works best.
                </p>
              </div>
            </div>
            <input ref={posterRef} type="file" accept="image/*" onChange={handlePosterUpload} className="hidden" />
          </section>

          <section>
            <p className="overline mb-8">Visibility</p>
            <label className="flex items-center gap-4 cursor-pointer">
              <input
                type="checkbox"
                name="is_published"
                checked={form.is_published}
                onChange={handleChange}
                data-testid="gp-is-published"
                className="w-5 h-5 accent-black"
              />
              <span className="text-sm">Published <span className="text-black/40">(unchecked = hidden from /gameplan)</span></span>
            </label>
          </section>

          <div className="flex flex-col sm:flex-row gap-4 pt-8 border-t border-black/10">
            <button
              type="submit"
              disabled={saving}
              data-testid="gp-save"
              className="inline-flex items-center justify-center px-12 py-4 bg-black text-white border border-black text-[11px] uppercase tracking-[0.28em] hover:bg-white hover:text-black transition-all duration-500 disabled:opacity-50"
            >
              {saving ? "Saving…" : isEdit ? "Save changes" : "Publish quarter"}
            </button>
            <Link
              to="/admin"
              className="inline-flex items-center justify-center px-10 py-4 bg-transparent text-black border border-black text-[11px] uppercase tracking-[0.28em] hover:bg-black hover:text-white transition-all duration-500"
            >
              Cancel
            </Link>
          </div>
        </form>
      </div>
    </main>
  );
};

export default GamePlanForm;
